/**
 * api 客户端（S04）。
 *
 * 通道：SDK callFunction 调云函数 `api`（事件模型，S03 单入口）。
 * 职责：
 *  1. 组装事件 {path, httpMethod, headers, body}
 *  2. 自动注入 Authorization（来自 auth.getSession() 的 access_token）
 *  3. POST 自动注入 Idempotency-Key（可选关闭）
 *  4. 统一包络 {code,msg,data,traceId} 解析；非 0 抛 ApiError（带 traceId）
 */

import { app, auth } from "./cloudbase";

export interface ApiEnvelope<T = unknown> {
  code: number;
  msg: string;
  data: T;
  traceId: string;
}

export class ApiError extends Error {
  code: number;
  traceId: string;
  constructor(code: number, msg: string, traceId: string) {
    super(msg);
    this.code = code;
    this.traceId = traceId;
  }
}

function traceId(): string {
  return crypto.randomUUID();
}

async function accessToken(): Promise<string | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (auth as any).getSession();
    return data?.session?.access_token ?? null;
  } catch {
    return null;
  }
}

export interface CallOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** POST 默认自动带 Idempotency-Key；显式传 false 关闭。 */
  idempotencyKey?: string | false;
  /** 额外请求头（如 x-tenant-id，S06 起统一从项目上下文注入）。 */
  headers?: Record<string, string>;
}

export async function callApi<T = unknown>(
  path: string,
  opts: CallOptions = {}
): Promise<ApiEnvelope<T>> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };

  const token = await accessToken();
  if (token) headers["authorization"] = `Bearer ${token}`;

  let body: string | undefined;
  if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (method === "POST" && opts.idempotencyKey !== false) {
    headers["idempotency-key"] =
      typeof opts.idempotencyKey === "string" ? opts.idempotencyKey : traceId();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = await (app as any).callFunction({
    name: "api",
    data: { path, httpMethod: method, headers, body: body ?? null },
  });

  const env: ApiEnvelope<T> = res?.result;
  if (!env || typeof env.code !== "number") {
    throw new ApiError(-1, "响应格式异常（缺少统一包络）", res?.requestId ?? "-");
  }
  if (env.code !== 0) {
    throw new ApiError(env.code, env.msg, env.traceId);
  }
  return env;
}
